// built-from: /contextual-facilitator-companion v2 impl:contextual-facilitator-companion role:implementation
// built-from: /surface-binding-contract v2 impl:surface-binding-contract role:implementation
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import SessionCapturePanel from '../components/SessionCapturePanel';
import { FacilitatorDock } from '../components/FacilitatorDock';
import FacilitatorReplyActions from './FacilitatorReplyActions';
import { useChat } from '../hooks/useChat';
import type { ScreenChatContext } from '../hooks/screenChatContext';
import type { SurfaceAction } from '../hooks/sessionTypes';
import { projectSurfaceFactState, useFactLog, type FactEvent } from './useFactLog';
import { useFacilitationExperience, type FacilitationExperience } from './useFacilitationExperience';
import { surfaceForId } from './surfaceRegistry';
import type { GatedFact, SurfaceEdit, SurfaceFactState, SurfaceFocus } from './surfaceBinding';
import '../styles/ContextualFacilitatorCompanion.css';

export interface AttachedScreenContext {
  revision: number;
  presentationRevision?: string;
  focus?: string | null;
  values: Record<string, unknown>;
}

export interface FacilitatorAcknowledgement {
  capabilityId: string;
  input: Record<string, unknown>;
  target?: GatedFact;
}

export interface FacilitatorActions {
  proposals?: {
    applyCapabilityId: string;
    dismissCapabilityId: string;
    applyLabel?: string;
    dismissLabel?: string;
  };
  completion?: { label: string };
}

export interface FacilitatorRegistration {
  screenId: string;
  profileId: string;
  resolveSession: () => string | null;
  getContext: () => AttachedScreenContext;
  allowedCapabilityIds: readonly string[];
  actions?: FacilitatorActions;
  // 100 (owner decision 7): true where the attached surface consumes
  // `highlightFactId` and can jump to a fact. A screen that cannot is honest
  // about it: its facts read as plain text, not as links that do nothing.
  // Omitted defers to the registered surface definition.
  canHighlight?: boolean;
  onCapabilityAcknowledged?: (acknowledgement: FacilitatorAcknowledgement) => void;
  onSurfaceAction?: (action: SurfaceAction) => boolean;
}

export interface FacilitatorAttachment {
  readonly registration: FacilitatorRegistration;
}

/** Public, React-free registration seam. Screens declare context and authority;
 * this function never starts chat, reads a log, or performs a write. */
export const attachFacilitator = (
  registration: FacilitatorRegistration,
): FacilitatorAttachment => ({ registration });

export interface AttachedFacilitatorController {
  sessionId: string;
  experience: FacilitationExperience | null;
  loading: boolean;
  error: string | null;
  openingTurn: boolean;
  state: SurfaceFactState;
  pendingCount: number;
  rejection: string | null;
  completion: { eligible: boolean; outstanding: string[] } | null;
  /** Whether the attached surface can go to a fact the conversation names. */
  canHighlight: boolean;
  chat: ReturnType<typeof useChat>;
  execute: (edit: SurfaceEdit, target?: GatedFact) => Promise<boolean>;
  publishFocus: (focus: SurfaceFocus) => void;
  activateFactChip: (factId: string) => void;
  complete: () => Promise<boolean>;
}

const AttachmentContext = createContext<AttachedFacilitatorController | null>(null);

function ControllerProvider({ attachment, sessionId, children }: {
  attachment: FacilitatorAttachment;
  sessionId: string;
  children: React.ReactNode;
}) {
  const { registration } = attachment;
  const { experience, error, loading } = useFacilitationExperience(sessionId);
  const replica = useFactLog(sessionId, Boolean(experience));
  const { applyTurnEvents, emitEdit } = replica;
  const [focus, setFocus] = useState<string | null>(() => registration.getContext().focus ?? null);
  const [lastTurnFactIds, setLastTurnFactIds] = useState<string[]>([]);
  const [highlightFactId, setHighlightFactId] = useState<string | null>(null);
  const [sealedLocally, setSealedLocally] = useState(false);
  const [openingTurn, setOpeningTurn] = useState(true);

  // 100 (owner decision 7): the turn's facts belong to the reply that produced
  // them, and the chat hook rides them on that message. Here they only cue the
  // surface's pulse; there is no standing band above the conversation.
  const handleTurnFactEvents = useCallback((incoming: unknown[]) => {
    const events = incoming as FactEvent[];
    const touched = new Set<string>();
    for (const event of events) {
      if (!event || typeof event.factId !== 'string' || !event.factId) continue;
      touched.add(event.factId);
    }
    applyTurnEvents(events);
    setLastTurnFactIds([...touched]);
    setHighlightFactId(null);
  }, [applyTurnEvents]);

  const live = registration.getContext();
  const liveFocus = live.focus ?? null;
  const observedLiveFocus = useRef(liveFocus);
  const liveFocusChanged = observedLiveFocus.current !== liveFocus;
  const outboundFocus = liveFocusChanged ? liveFocus : focus;
  useEffect(() => {
    if (liveFocusChanged) {
      observedLiveFocus.current = liveFocus;
      setFocus(liveFocus);
    }
  }, [liveFocus, liveFocusChanged]);
  const screenContext = useMemo(() => ({
    schemaVersion: 1 as const,
    screenId: registration.screenId,
    revision: live.revision,
    presentationRevision: live.presentationRevision,
    focus: outboundFocus,
    values: live.values,
    allowedCapabilityIds: [...registration.allowedCapabilityIds],
    // Additive compatibility fields consumed by the incumbent chat hook type.
    entities: [],
    selection: outboundFocus,
    captured: [],
    outstanding: [],
  }), [live.presentationRevision, live.revision, live.values, outboundFocus, registration.allowedCapabilityIds, registration.screenId]);

  const definition = surfaceForId(experience?.binding.surfaceId);
  const chat = useChat({
    kind: 'facilitation',
    sessionId,
    screenContext: screenContext as ScreenChatContext,
    onTurnFactEvents: handleTurnFactEvents,
    onSurfaceActions: (actions) => actions.forEach((action) => registration.onSurfaceAction?.(action)),
    copy: definition?.chatCopy,
    speakReplies: definition?.voice?.speakReplies,
  });

  const openingFired = useRef(false);
  useEffect(() => {
    if (openingFired.current || !experience || !chat.historySeeded) return;
    openingFired.current = true;
    void (chat.conversationHistory.length ? chat.resumeSession() : chat.openSession())
      .catch(() => {})
      .finally(() => setOpeningTurn(false));
  }, [chat, experience]);

  const state = useMemo<SurfaceFactState>(() => ({
    ...projectSurfaceFactState(replica.events, replica.pending),
    sealed: replica.sealed || sealedLocally,
    ...(highlightFactId ? { highlightFactId } : {}),
    ...(lastTurnFactIds.length ? { lastTurnFactIds } : {}),
  }), [highlightFactId, lastTurnFactIds, replica.events, replica.pending, replica.sealed, sealedLocally]);

  const execute = useCallback(async (edit: SurfaceEdit, target?: GatedFact) => {
    if (!registration.allowedCapabilityIds.includes(edit.capabilityId)) return false;
    setLastTurnFactIds([]);
    setHighlightFactId(null);
    const acknowledged = await emitEdit(edit);
    if (acknowledged) {
      registration.onCapabilityAcknowledged?.({
        capabilityId: edit.capabilityId,
        input: edit.input,
        target,
      });
    }
    return acknowledged;
  }, [emitEdit, registration]);

  const completionCapabilityId = experience?.completionCapabilityId;
  const complete = useCallback(async () => {
    if (!completionCapabilityId) return false;
    const acknowledged = await execute({ capabilityId: completionCapabilityId, input: {} });
    if (acknowledged) setSealedLocally(true);
    return acknowledged;
  }, [completionCapabilityId, execute]);

  const controller = useMemo<AttachedFacilitatorController>(() => ({
    sessionId,
    experience,
    loading,
    error,
    openingTurn,
    state,
    pendingCount: replica.pending.length,
    rejection: replica.rejection,
    completion: replica.completion,
    canHighlight: registration.canHighlight ?? Boolean(definition?.canHighlight),
    chat: {
      ...chat,
      inputBusy: Boolean(chat.inputBusy || replica.sealed || sealedLocally),
    },
    execute,
    publishFocus: (next) => setFocus(next.factId),
    activateFactChip: setHighlightFactId,
    complete,
  }), [chat, complete, definition?.canHighlight, error, execute, experience, loading, openingTurn,
    registration.canHighlight, replica.completion, replica.pending.length, replica.rejection,
    replica.sealed, sealedLocally, sessionId, state]);

  return <AttachmentContext.Provider value={controller}>{children}</AttachmentContext.Provider>;
}

/** Keying the controller by profile + session makes unmount the cancellation
 * boundary: a late turn from a previous registration cannot reach a new screen. */
export function FacilitatorAttachmentProvider({ attachment, children }: {
  attachment: FacilitatorAttachment;
  children: React.ReactNode;
}) {
  const sessionId = attachment.registration.resolveSession();
  if (!sessionId) return <>{children}</>;
  return (
    <ControllerProvider
      key={`${attachment.registration.profileId}:${sessionId}`}
      attachment={attachment}
      sessionId={sessionId}
    >
      {children}
    </ControllerProvider>
  );
}

export function useAttachedFacilitator(): AttachedFacilitatorController {
  const controller = useContext(AttachmentContext);
  if (!controller) throw new Error('useAttachedFacilitator must be used inside FacilitatorAttachmentProvider');
  return controller;
}

/** The same controller for a screen that legitimately renders before its
 * conversation exists (100, decision 10): null outside a provider rather than
 * a throw, so no caller needs a try/catch around a hook. */
export function useAttachedFacilitatorOrNull(): AttachedFacilitatorController | null {
  return useContext(AttachmentContext);
}

export function ContextualFacilitatorCompanion({ onClose, className = '', label }: {
  onClose: () => void;
  className?: string;
  label?: string;
}) {
  const controller = useAttachedFacilitator();
  const [confirmingCompletion, setConfirmingCompletion] = useState(false);
  // The registration actions are recovered from the provider through the
  // current experience-independent controller callbacks below.
  const attachment = useContext(RegistrationContext);
  const actions = attachment?.registration.actions;
  const proposalActions = actions?.proposals;
  const definition = surfaceForId(controller.experience?.binding.surfaceId);

  if (controller.loading) return <aside className={`fshell-rail ${className}`} role="status">Opening ArcPilot facilitator...</aside>;
  if (controller.error || !controller.experience) {
    return (
      <aside className={`fshell-rail ${className}`} role="alert">
        <strong>ArcPilot facilitator could not open</strong>
        <p>{controller.error || 'This conversation is unavailable.'}</p>
        <button type="button" onClick={onClose}>Close facilitator</button>
      </aside>
    );
  }

  return (
    <FacilitatorDock className={className} label={label || definition?.chatLabel || 'ArcPilot facilitator'}>
      {definition?.about?.length ? (
        <details className="fshell-about"><summary className="fshell-about-summary">About this session</summary>
          {definition.about.map((paragraph) => <p key={paragraph.slice(0, 24)} className="fshell-about-copy">{paragraph}</p>)}
        </details>
      ) : null}
      {controller.rejection ? <div role="alert" className="fshell-alert">{controller.rejection}</div> : null}
      {proposalActions && controller.state.drafts.length > 0 ? (
        <div className="contextual-facilitator-proposals" aria-label="Suggestions to review">
          {controller.state.drafts.map((fact) => (
            <article key={fact.factId}>
              <p>{typeof fact.content.text === 'string' ? fact.content.text : 'Suggested update'}</p>
              <div>
                <button type="button" onClick={() => void controller.execute({
                  capabilityId: proposalActions.applyCapabilityId,
                  input: { factId: fact.factId },
                }, fact)}>{proposalActions.applyLabel || 'Apply'}</button>
                <button type="button" onClick={() => void controller.execute({
                  capabilityId: proposalActions.dismissCapabilityId,
                  input: { factId: fact.factId },
                }, fact)}>{proposalActions.dismissLabel || 'Dismiss'}</button>
              </div>
            </article>
          ))}
        </div>
      ) : null}
      <SessionCapturePanel
        session={controller.chat}
        className="fshell-rail-panel"
        ariaLabel="Facilitated conversation"
        inputLabel="Ask or add context"
        emptyText={definition?.emptyChatText || 'Start the conversation.'}
        facilitationAttachments
        renderAgentFooter={(message) => (
          <FacilitatorReplyActions
            reply={message.text}
            sessionId={controller.sessionId}
            citations={message.citations}
            facts={message.facts}
            canHighlight={controller.canHighlight}
            onHighlight={controller.activateFactChip}
          />
        )}
      />
      {actions?.completion ? (
        <button
          type="button"
          className="fshell-close"
          disabled={controller.state.sealed || controller.completion?.eligible !== true}
          onClick={() => setConfirmingCompletion(true)}
        >
          {controller.state.sealed ? 'Session completed' : actions.completion.label}
        </button>
      ) : null}
      {confirmingCompletion ? (
        <div className="ap-modal-backdrop" onClick={() => setConfirmingCompletion(false)}>
          <div
            className="ap-modal fshell-close-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Complete session"
            onClick={(event) => event.stopPropagation()}
          >
            <p className="fshell-close-copy">Complete this session? It will become read-only.</p>
            <div className="fshell-close-actions">
              <button type="button" className="ap-modal-cancel" autoFocus onClick={() => setConfirmingCompletion(false)}>Cancel</button>
              <button type="button" className="ap-modal-confirm" onClick={() => {
                setConfirmingCompletion(false);
                void controller.complete();
              }}>Complete session</button>
            </div>
          </div>
        </div>
      ) : null}
    </FacilitatorDock>
  );
}

const RegistrationContext = createContext<FacilitatorAttachment | null>(null);

export function AttachedFacilitator({ attachment, children }: {
  attachment: FacilitatorAttachment;
  children: React.ReactNode;
}) {
  return (
    <RegistrationContext.Provider value={attachment}>
      <FacilitatorAttachmentProvider attachment={attachment}>{children}</FacilitatorAttachmentProvider>
    </RegistrationContext.Provider>
  );
}
